import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useEventCallback, useLiveRef } from '@gitbook/util-react-hooks';

type KeyedItem = {
    key: string;
};

function checkIsKeyedItem(item: unknown): item is KeyedItem {
    return Boolean(
        typeof item === 'object' && item && 'key' in item && typeof item.key === 'string'
    );
}

function defaultKeyGetter(item: unknown): string {
    if (!checkIsKeyedItem(item)) {
        throw new Error('Need a key getter');
    }
    return item.key;
}

type ListCursor<T> = {
    next: T | null;
    prev: T | null;
    current: T | null;
};

type CursorInfo = {
    index: number;
};

type SetCursorFn<T> = (item: T) => void;

/**
 * Hook to handle keyboard navigation in a list
 * - up/down to go through the items
 * - enter to select one
 */
export function useListKeyboardNavigation<T>(options: {
    items: T[];

    active?: boolean;

    /**
     * If true, the cursor will start with the first element instead of none.
     * @default false
     */
    startAtFirst?: boolean;

    /**
     * Callback to scroll to a specific item.
     */
    scrollTo: (item: T) => void;

    /**
     * Callback when pressing enter on an item.
     * If not provided, pressing enter will not do anything.
     */
    onSelect?: (item: T, info: CursorInfo) => void;

    /**
     * Callback when pressing the right arrow on an item to open a potential nesting.
     */
    onOpen?: (item: T, info: CursorInfo) => void;

    /**
     * Callback to close the palette.
     */
    onClose?: () => void;

    /**
     * Get unique key for an item.
     */
    getKey?: (item: T) => string;

    /**
     * Define which key should be used to submit the current item.
     * @default 'Enter'
     */
    submit?: 'Enter' | 'Tab';

    /**
     * If yes, lateral navigation will be enabled and use between items.
     * @default true
     */
    lateralNavigation?: boolean;
}): {
    cursor: ListCursor<T>;
    setCursor: SetCursorFn<T>;
    hoverItem: SetCursorFn<T>;
    onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void;
} {
    const {
        items,
        scrollTo,
        getKey = defaultKeyGetter,
        active = true,
        startAtFirst = false,
        lateralNavigation = true,
        submit = 'Enter',
    } = options;
    const [cursorKey, setCursorKey] = useState<string | null>(null);
    const [hasMouseMovedSinceKeyboard, setHasMouseMovedSinceKeyboard] = useState(false);
    const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null);
    const optionsRef = useLiveRef(options);

    // Locate the currently selected item and the navigation arround it.
    const cursor = useMemo(
        () => computeCursor(items, cursorKey, getKey, startAtFirst),
        [items, cursorKey, getKey, startAtFirst]
    );

    const getCursor = useEventCallback(() => cursor);
    const getCursorInfo = useEventCallback((item: T): CursorInfo => {
        const itemKey = getKey(item);
        const index = items.findIndex((item) => getKey(item) === itemKey);
        return { index };
    });
    const setCursor = useEventCallback((item: T) => setCursorKey(getKey(item)));
    const hoverItem = useEventCallback((item: T) => {
        // Prevent hovers from overriding the active command item when using arrow-up/down keys to navigate the items
        if (!hasMouseMovedSinceKeyboard) {
            return;
        }

        setCursor(item);
    });
    const moveCursor = useEventCallback((move: 1 | -1) => {
        const { next, prev } = cursor;

        setHasMouseMovedSinceKeyboard(false);
        if (move > 0 && next) {
            setCursor(next);
            scrollTo(next);
        } else if (move < 0 && prev) {
            setCursor(prev);
            scrollTo(prev);
        }
    });

    useEffect(() => {
        if (!active) {
            return;
        }

        const listener = (event: KeyboardEvent) => {
            const { onSelect, onClose, onOpen } = optionsRef.current;
            const cursor = getCursor();
            // We prevent default and stop propagation to avoid the event
            // being handled by slate which is binded to react global events handler
            if (event.key === submit && onSelect && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                if (cursor.current) {
                    onSelect(cursor.current, getCursorInfo(cursor.current));
                }
                return;
            }

            if (lateralNavigation) {
                if (event.key === 'ArrowRight' && isTypingAtEdge(event, 'end')) {
                    event.preventDefault();
                    event.stopPropagation();

                    if (cursor.current) {
                        // If the item doesn't have a descendant, we close the palette
                        // @ts-ignore - cursor.current is the current palette item
                        const hasNoDescendant = !cursor.current.descendant;
                        if (hasNoDescendant) {
                            onClose?.();
                            return;
                        }

                        // If the item has a descendant, we open it
                        onOpen?.(cursor.current, getCursorInfo(cursor.current));
                        return;
                    }

                    // If no item is selected, we select the first one
                    moveCursor(1);
                }

                if (event.key === 'ArrowLeft' && isTypingAtEdge(event, 'start')) {
                    event.preventDefault();
                    event.stopPropagation();
                    onClose?.();
                    return;
                }
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                moveCursor(1);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                moveCursor(-1);
            }
        };

        document.addEventListener('keydown', listener, { capture: true });
        return () => {
            document.removeEventListener('keydown', listener, { capture: true });
        };
    }, [moveCursor, getCursorInfo, active, getCursor, optionsRef, submit, lateralNavigation]);

    // Track mouse to prevent hovers from overriding the active command item when navigating the palette using arrow-up/down keys.
    const onMouseMove = useEventCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const currentX = event.clientX;
        const currentY = event.clientY;

        // If we haven't tracked mouse position yet, just set it
        if (!lastMousePositionRef.current) {
            lastMousePositionRef.current = { x: currentX, y: currentY };
            return;
        }

        // Check if mouse has moved at least 1 pixel in any direction
        const { x: lastX, y: lastY } = lastMousePositionRef.current;

        if (Math.abs(currentX - lastX) >= 1 || Math.abs(currentY - lastY) >= 1) {
            setHasMouseMovedSinceKeyboard(true);
            lastMousePositionRef.current = { x: currentX, y: currentY };
        }
    });

    return { cursor, setCursor, hoverItem, onMouseMove };
}

/**
 * Compute the state for the cursor.
 */
function computeCursor<T>(
    items: T[],
    cursorKey: string | null,
    getKey: (item: T) => string,
    startAtFirst: boolean
): ListCursor<T> {
    let prev: T | null = null;
    let next: T | null = null;
    let current: T | null = null;

    items.some((item) => {
        if (getKey(item) === cursorKey) {
            current = item;
        } else if (!current) {
            prev = item;
        } else if (current && !next) {
            next = item;
        }

        return current != null && next != null;
    });

    // Default to start with first item
    if (!current) {
        if (startAtFirst) {
            prev = null;
            current = items[0];
            next = items[1];
        } else {
            prev = null;
            current = null;
            next = items[0];
        }
    }

    return {
        prev,
        next,
        current,
    };
}

/**
 * When user is typing ArrowLeft or ArrowRight,
 * we want to check that it's not going to impact the input being edited.
 */
function isTypingAtEdge(event: KeyboardEvent, edge: 'start' | 'end'): boolean {
    const target = event.target;

    if (!target) {
        return true;
    }

    const selectionStart = (target as HTMLInputElement).selectionStart;

    // If the target is not an editable element, we assume the user is at the edge.
    if (selectionStart === null || selectionStart === undefined) {
        return true;
    }

    const selectionEnd = (target as HTMLInputElement).selectionEnd;

    // Ensure the selection is collapsed
    if (selectionStart !== selectionEnd) {
        return false;
    }

    if (edge === 'start') {
        return selectionStart === 0;
    }

    const valueLength = (target as HTMLInputElement).value.length;
    return selectionStart === valueLength;
}
